import { useCallback, useEffect, useState } from 'react';
import { Button, Center, Group, Loader, Stack, Text } from '@mantine/core';
import { IconLogout } from '@tabler/icons-react';
import { useAuth } from '@workos-inc/authkit-react';
import { Navigate, Outlet } from 'react-router-dom';
import { fetchMe } from '../api/me';
import { isOrgAdmin } from '../lib/permissions';
import { resolveTermsGate, type TermsState } from '../lib/routing';
import { useTermsGateEnabled } from '../lib/featureFlags';
import { logEvent } from '../lib/telemetry';

// Wraps the Hub subtree only (see App.tsx), so members' and staff routes are never gated here.
// An org admin whose org has not accepted the terms is held on a plain loader while /me resolves,
// then redirected to /terms — the Hub chrome never renders first. Fail-closed on a /me error.
export function TermsGate() {
  const auth = useAuth();
  const orgAdmin = isOrgAdmin(auth.permissions);
  const gateEnabled = useTermsGateEnabled();
  const [terms, setTerms] = useState<TermsState>('pending');

  // Mirrors EnterpriseHub's load pattern: some AuthKit providers change the auth object identity
  // every render, so we deliberately do not depend on `auth` — re-checking on org change instead.
  const load = useCallback(() => {
    if (!gateEnabled || !orgAdmin) return; // gate off, or a member — skip the fetch entirely
    const ctrl = new AbortController();
    void (async () => {
      setTerms('pending');
      try {
        const token = await auth.getAccessToken();
        if (!token) { setTerms('error'); return; }
        const me = await fetchMe(token, ctrl.signal);
        setTerms(me.terms?.accepted === true ? 'accepted' : 'not-accepted');
      } catch (e) {
        if ((e as Error).name === 'AbortError') return;
        setTerms('error');
      }
    })();
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-run on org change; token read at call time
  }, [gateEnabled, orgAdmin, auth.organizationId]);

  useEffect(() => load(), [load]);

  // Gate off (kill switch) → never hold or redirect anyone; render the Hub.
  const decision = gateEnabled ? resolveTermsGate(orgAdmin, terms) : 'render';

  useEffect(() => {
    if (decision === 'gate') {
      logEvent('cloudhouse.enterprise.terms.gated', 'Gated org admin to the terms page', {
        org_id: auth.organizationId ?? null,
        user_id: auth.user?.id ?? null,
      });
    } else if (decision === 'error') {
      logEvent('cloudhouse.enterprise.terms.load_failed', 'Could not load terms status at the gate', {
        org_id: auth.organizationId ?? null,
      });
    }
  }, [decision, auth.organizationId, auth.user?.id]);

  if (decision === 'pending') {
    return (
      <Center h="100vh" role="progressbar" aria-label="Loading">
        <Loader size="xl" />
      </Center>
    );
  }
  if (decision === 'error') {
    return (
      <Center h="100vh">
        <Stack align="center" gap="md" maw={420} ta="center">
          <Text>We couldn't check your organisation's terms status. Please try again.</Text>
          <Group gap="sm">
            <Button onClick={load}>Retry</Button>
            {/* A dead-end otherwise: no Hub chrome renders here, so sign-out is the only other way out. */}
            <Button variant="subtle" color="gray" leftSection={<IconLogout size={16} stroke={1.8} />} onClick={() => auth.signOut()}>
              Sign out
            </Button>
          </Group>
        </Stack>
      </Center>
    );
  }
  if (decision === 'gate') return <Navigate to="/terms" replace />;
  return <Outlet />;
}
